import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  TipBox, CheckItem, VideoDrive,
  PageHero, ContentWrap, SectionTitle, SubTitle, Card,
} from '../shared';

export default function SaudeBucalSaudeGeral() {
  const isMobile = useIsMobile();

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Saúde Bucal e Saúde Geral"
        subtitle="A saúde da boca pode influenciar a saúde do corpo. Problemas bucais podem afetar a alimentação, a fala e a qualidade de vida."
        audio="saude-bucal-saude-geral.m4a"
      />

      <ContentWrap>

        {/* Intro */}
        <p style={{ lineHeight: 1.75, marginTop: 0 }}>
          Muitas pessoas pensam que a boca é separada do restante do organismo, mas não é.
          Inflamações e infecções na boca podem afetar outras partes do corpo e dificultar o
          controle de algumas doenças. A saúde bucal e a saúde geral estão conectadas,
          especialmente em adultos e idosos que convivem com doenças crônicas.
        </p>

        <SectionTitle>Saúde bucal, diabetes e doenças cardiovasculares</SectionTitle>
        <VideoDrive
          id="1wR14iQbzfyOFSaHRMmKAcpa-zRIOArzF"
          title="Saúde bucal, diabetes e doenças cardiovasculares"
        />

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Diabetes */}
        <SectionTitle>Saúde Bucal e Diabetes</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          A diabetes acontece quando o organismo tem dificuldade para controlar a quantidade de
          açúcar no sangue. Quando a diabetes não está bem controlada, o corpo fica mais vulnerável
          a infecções, inclusive na boca.
        </p>

        <SubTitle>Pessoas com diabetes podem apresentar:</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            'Gengiva inchada e sangrando',
            'Maior risco de gengivite e periodontite',
            'Mau hálito',
            'Boca seca',
            'Maior acúmulo de placa bacteriana',
            'Feridas que demoram para cicatrizar',
            'Maior risco de infecções',
            'Dentes amolecidos em casos avançados',
          ].map((item, i) => (
            <Card key={i} variant={i} style={{ padding: '10px 14px', fontSize: '0.93rem' }}>
              🔴 {item}
            </Card>
          ))}
        </div>

        <p style={{ lineHeight: 1.75 }}>
          O excesso de açúcar no sangue favorece inflamações e dificulta a defesa do organismo
          contra bactérias. Além disso, a diabetes pode reduzir a capacidade de cicatrização do
          corpo.
        </p>

        <div
          style={{
            background: AI_COLORS.bgBlue50,
            borderRadius: 12,
            padding: '18px 22px',
            margin: '20px 0',
            borderLeft: `4px solid ${AI_COLORS.blue400}`,
          }}
        >
          <p
            style={{
              fontFamily: AI_FONTS.title,
              color: AI_COLORS.blue900,
              fontSize: '1rem',
              margin: '0 0 10px',
            }}
          >
            A relação é de mão dupla:
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2 }}>
            <li>Diabetes descontrolada piora a saúde bucal</li>
            <li>
              Problemas bucais (gengivite, periodontite) podem dificultar o controle da diabetes,
              pois a inflamação constante dificulta o controle da glicemia
            </li>
          </ul>
        </div>

        <TipBox>
          Por isso, cuidar da boca ajuda também no controle da doença. Manter uma boa higiene
          bucal, usar fio dental e realizar acompanhamento com o dentista fazem parte do tratamento
          integral do diabetes.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Cardiovascular */}
        <SectionTitle>Saúde Bucal e Doenças Cardiovasculares</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          As doenças cardiovasculares afetam o coração e os vasos sanguíneos, como pressão alta,
          infarto, AVC e problemas circulatórios.
        </p>
        <p style={{ lineHeight: 1.75 }}>
          As doenças gengivais, principalmente a periodontite, podem aumentar processos
          inflamatórios no organismo. As bactérias e inflamações da gengiva podem entrar na
          corrente sanguínea e contribuir para alterações nos vasos sanguíneos.
        </p>

        <TipBox>
          Isso não significa que toda pessoa com problema gengival terá doença no coração, mas a
          saúde bucal pode influenciar a saúde cardiovascular.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Sinais de alerta */}
        <SectionTitle>Sinais de Alerta na Boca</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>Fique atento a:</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 10,
            marginBottom: 20,
          }}
        >
          {[
            'Gengiva sangrando',
            'Mau hálito persistente',
            'Dentes moles',
            'Dor gengival',
            'Gengiva se afastando e expondo as raízes dos dentes',
            'Feridas que demoram para cicatrizar',
            'Boca muito seca',
          ].map((sinal, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.pink50,
                borderRadius: 8,
                padding: '10px 14px',
                borderLeft: '3px solid #EF9A9A',
                fontSize: '0.93rem',
              }}
            >
              ⚠️ {sinal}
            </div>
          ))}
        </div>
        <p style={{ lineHeight: 1.75 }}>
          Esses sinais merecem avaliação do cirurgião dentista.
        </p>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Prevenção */}
        <SectionTitle>Como Prevenir?</SectionTitle>
        <ul style={{ padding: 0, margin: '0 0 16px' }}>
          <CheckItem>
            <strong>Escove os dentes diariamente</strong>, pelo menos 3 vezes ao dia.
          </CheckItem>
          <CheckItem>
            <strong>Use fio dental</strong>, ele remove a placa bacteriana entre os dentes.
          </CheckItem>
          <CheckItem>
            <strong>Faça acompanhamento odontológico regular</strong>, o dentista identifica
            problemas precocemente.
          </CheckItem>
          <CheckItem>
            <strong>Controle a diabetes corretamente</strong>, seguir o tratamento médico ajuda
            também na saúde da boca.
          </CheckItem>
          <CheckItem>
            <strong>Tenha alimentação equilibrada</strong>
          </CheckItem>
          <CheckItem>
            <strong>Evite cigarro</strong>, o tabagismo aumenta muito o risco de doenças gengivais
            e cardiovasculares.
          </CheckItem>
        </ul>

        <div
          style={{
            background: AI_COLORS.bgBlue50,
            borderLeft: `4px solid ${AI_COLORS.blue400}`,
            borderRadius: 10,
            padding: '18px 22px',
            marginTop: 24,
          }}
        >
          <p
            style={{
              fontFamily: AI_FONTS.title,
              color: AI_COLORS.blue900,
              fontSize: '1.05rem',
              margin: '0 0 8px',
            }}
          >
            Mensagem central
          </p>
          <p style={{ margin: 0, lineHeight: 1.75 }}>
            Cuidar da saúde bucal é cuidar do corpo inteiro. Uma boca saudável ajuda no bem-estar,
            na alimentação, na fala e na saúde geral do organismo. Informe sempre seus médicos e
            dentistas sobre todas as condições de saúde e medicamentos que usa.
          </p>
        </div>
      </ContentWrap>
    </div>
  );
}
